import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { transactionAPI } from '../../services/api';
import DetailsPanel from '../../components/ui/DetailsPanel';

const TransactionHistory = () => {
  const { id } = useParams();
  const history = useHistory();
  const [transaction, setTransaction] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch transaction details and history
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch both transaction details and history
        const [transactionResponse, historyResponse] = await Promise.all([
          transactionAPI.getById(id),
          transactionAPI.getHistory(id)
        ]);
        
        
        setTransaction(transactionResponse.data);
        
        // Process timeline events from backend
        const timelineEvents = historyResponse.data || [];
        
        // Transform backend timeline events to frontend format
        const processedEvents = timelineEvents.map(event => {
          // Determine event type
          let eventType = 'other';
          let description = event.action;
          let verificationType = null; // 'CLIENT' or 'COMPTABILITE' or null
          
          if (event.action === 'TRANSACTION_CREATED') {
            eventType = 'created';
            description = 'Transaction créée';
          } else if (event.action === 'DESCRIPTION_UPDATED') {
            eventType = 'description_changed';
            description = 'Description modifiée';
          // Legacy VERIFICATION_STATUS_UPDATED - commented out after migration
          // } else if (event.action === 'VERIFICATION_STATUS_UPDATED') {
          //   eventType = 'verification_status_changed';
          //   description = 'Statut de vérification modifié';
          } else if (event.action === 'VERIFICATION_STATUS_CLIENT_UPDATED') {
            eventType = 'verification_status_changed';
            description = 'Statut de vérification client modifié';
            verificationType = 'CLIENT';
          } else if (event.action === 'VERIFICATION_STATUS_COMPTABILITE_UPDATED') {
            eventType = 'verification_status_changed';
            description = 'Statut de vérification comptabilité modifié';
            verificationType = 'COMPTABILITE';
          } else if (event.action === 'REFS_UPDATED') {
            eventType = 'refs_changed';
            description = 'Références modifiées';
          } else if (event.action === 'RAPPROCHEMENT_CONTEXT') {
            eventType = 'rapprochement_context';
            description = 'Contexte rapprochement';
          } else if (event.action === 'VERSEMENT_CONTEXT') {
            eventType = 'versement_context';
            description = 'Contexte confirmation versement';
          } else if (event.action === 'VERSEMENT_CONFIRMED') {
            eventType = 'versement_confirmed';
            description = 'Versement confirmé';
          } else if (event.action === 'RAPPROCHEMENT_LINKED') {
            eventType = 'rapprochement_linked';
            description = 'Rapprochement';
          }
          
          return {
            id: event.id,
            type: eventType,
            agent: event.createdBy ? 
              `${event.createdBy.firstName} ${event.createdBy.lastName}` : 
              'Utilisateur inconnu',
            timestamp: event.createdAt,
            description: description,
            verificationType: verificationType,
            oldValue: event.oldValue,
            newValue: event.newValue
          };
        });
        
        setHistoryEvents(processedEvents);
      } catch (error) {
        console.error('Error fetching transaction history:', error);
        setHistoryEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleClose = () => {
    history.goBack();
  };

  const formatHistoryDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  if (loading) {
    return (
      <DetailsPanel onClose={handleClose}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>Chargement...</Typography>
        </Box>
      </DetailsPanel>
    );
  }

  if (!transaction) {
    return (
      <DetailsPanel onClose={handleClose}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>Transaction non trouvée</Typography>
        </Box>
      </DetailsPanel>
    );
  }

  return (
    <DetailsPanel onClose={handleClose}>
      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Montserrat',
          fontSize: '22px',
          fontWeight: 600,
          color: '#000000',
          textTransform: 'none',
          marginBottom: '20px'
        }}
      >
        Historique de la transaction
      </Typography>

      {/* Timeline */}
      <Box sx={{ position: 'relative' }}>
        {/* Timeline Line */}
        <Box
          sx={{
            position: 'absolute',
            left: '5px',
            top: '0px',
            width: '2px',
            height: '100%',
            backgroundColor: '#E3E3E3',
            zIndex: 1
          }}
        />

        {/* Timeline Events */}
        {historyEvents.map((event, index) => (
          <Box key={event.id} sx={{ position: 'relative', marginBottom: '20px', paddingLeft: '30px' }}>
            {/* Timeline Dot */}
            <Box
              sx={{
                position: 'absolute',
                left: '0px',
                top: '0px',
                width: '12px',
                height: '12px',
                backgroundColor: '#027BC6',
                borderRadius: '50%',
                zIndex: 2,
                boxShadow: '0px 20px 80px #00000011'
              }}
            />

            {/* Event Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#38485C',
                    textTransform: 'none'
                  }}
                >
                  {event.agent}
                </Typography>
                {/* Type pill */}
                <Box
                  sx={{
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontFamily: 'Montserrat',
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'none',
                    lineHeight: 1.6,
                    color: (event.type === 'created') ? '#0B5C2B' : (event.type === 'description_changed') ? '#5C3C0B' : (event.type === 'verification_status_changed') ? '#0B3C5C' : (event.type === 'refs_changed') ? '#5C4A0B' : (event.type === 'rapprochement_context') ? '#3B0B5C' : (event.type === 'versement_context') ? '#5C3B00' : (event.type === 'versement_confirmed') ? '#0B5C4A' : (event.type === 'rapprochement_linked') ? '#0A2540' : '#38485C',
                    backgroundColor: (event.type === 'created') ? '#E6F4EA' : (event.type === 'description_changed') ? '#FFF2E0' : (event.type === 'verification_status_changed') ? '#E6F0FA' : (event.type === 'refs_changed') ? '#FFF8E0' : (event.type === 'rapprochement_context') ? '#F3E6FA' : (event.type === 'versement_context') ? '#FFF3E0' : (event.type === 'versement_confirmed') ? '#E6FAF5' : (event.type === 'rapprochement_linked') ? '#E8EFF6' : '#F1F5F9',
                    border: '1px solid',
                    borderColor: (event.type === 'created') ? '#B7E0C3' : (event.type === 'description_changed') ? '#F6D7B0' : (event.type === 'verification_status_changed') ? '#C8D8F0' : (event.type === 'refs_changed') ? '#F0DFA0' : (event.type === 'rapprochement_context') ? '#D8B0F0' : (event.type === 'versement_context') ? '#FFD9A0' : (event.type === 'versement_confirmed') ? '#B0F0E0' : (event.type === 'rapprochement_linked') ? '#B8CEDE' : '#E2E8F0'
                  }}
                >
                  {event.type === 'created' ? 'Création' : event.type === 'description_changed' ? 'Modification' : event.type === 'verification_status_changed' ? 'Statut de vérification' : event.type === 'refs_changed' ? 'Références' : event.type === 'rapprochement_context' ? 'Contexte rapprochement' : event.type === 'versement_context' ? 'Contexte confirmation versement' : event.type === 'versement_confirmed' ? 'Confirmation' : event.type === 'rapprochement_linked' ? 'Rapprochement' : 'Événement'}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#445E76',
                  textTransform: 'none',
                  opacity: 0.88
                }}
              >
                {formatHistoryDate(event.timestamp)}
              </Typography>
            </Box>

            {/* Event Content */}
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E3E3E374',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0px 20px 80px #00000011'
              }}
            >
              {event.type === 'created' && (
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#000000',
                    textTransform: 'none',
                    opacity: 1
                  }}
                >
                  Transaction créée
                </Typography>
              )}

              {event.type === 'description_changed' && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#000000',
                      textTransform: 'none',
                      opacity: 1,
                      marginBottom: '8px'
                    }}
                  >
                    Description modifiée de :
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#707070',
                      textTransform: 'none',
                      opacity: 0.88,
                      lineHeight: '22px',
                      marginBottom: '8px'
                    }}
                  >
                    {event.oldValue || 'N/A'}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#000000',
                      textTransform: 'none',
                      opacity: 1,
                      marginBottom: '8px'
                    }}
                  >
                    À :
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#707070',
                      textTransform: 'none',
                      opacity: 0.88,
                      lineHeight: '22px'
                    }}
                  >
                    {event.newValue || 'N/A'}
                  </Typography>
                </Box>
              )}

          {event.type === 'verification_status_changed' && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000000',
                  textTransform: 'none',
                  opacity: 1,
                  marginBottom: '8px'
                }}
              >
                {event.verificationType === 'CLIENT' ? 'Statut de vérification client modifié de :' :
                 event.verificationType === 'COMPTABILITE' ? 'Statut de vérification comptabilité modifié de :' :
                 'Statut de vérification modifié de :'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#707070',
                  textTransform: 'none',
                  opacity: 0.88,
                  lineHeight: '22px',
                  marginBottom: '8px'
                }}
              >
                {event.oldValue || 'N/A'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000000',
                  textTransform: 'none',
                  opacity: 1,
                  marginBottom: '8px'
                }}
              >
                À :
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#707070',
                  textTransform: 'none',
                  opacity: 0.88,
                  lineHeight: '22px'
                }}
              >
                {event.newValue || 'N/A'}
              </Typography>
            </Box>
          )}

              {event.type === 'refs_changed' && (
                <Box>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#000000', textTransform: 'none', marginBottom: '8px' }}>
                    Références modifiées de :
                  </Typography>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#707070', textTransform: 'none', opacity: 0.88, lineHeight: '22px', marginBottom: '8px' }}>
                    {event.oldValue || '(aucune)'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#000000', textTransform: 'none', marginBottom: '8px' }}>
                    À :
                  </Typography>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#707070', textTransform: 'none', opacity: 0.88, lineHeight: '22px' }}>
                    {event.newValue || '(aucune)'}
                  </Typography>
                </Box>
              )}

              {event.type === 'rapprochement_context' && (() => {
                let ctx = {};
                try { ctx = JSON.parse(event.newValue || '{}'); } catch (e) {}
                const isAdd = ctx.direction === 'ADD';
                const fmtAmt = (v) => v ? Number(v).toLocaleString('fr-MA') + ' MAD' : '—';
                const fmtSigned = (v, add) => {
                  if (!v) return '—';
                  const n = Number(v);
                  const color = n >= 0 ? '#0AD69F' : '#EF456E';
                  const prefix = n >= 0 ? '+' : '';
                  return <Typography component="span" sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color, textTransform: 'none' }}>{prefix}{n.toLocaleString('fr-MA')} MAD</Typography>;
                };
                const Row = ({ label, children }) => (
                  <Box sx={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#445E76', textTransform: 'none' }}>{label} :</Typography>
                    {typeof children === 'string'
                      ? <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#000000', textTransform: 'none' }}>{children}</Typography>
                      : children}
                  </Box>
                );

                if (ctx.type === 'VERSEMENT_CONFIRMATION') {
                  return (
                    <Box>
                      {ctx.versementCode && <Row label="Versement">{ctx.versementCode}</Row>}
                      {ctx.montantDeclare && <Row label="Montant déclaré">{fmtAmt(ctx.montantDeclare)}</Row>}
                      {ctx.montantConstate && <Row label="Montant constaté">{fmtAmt(ctx.montantConstate)}</Row>}
                      {ctx.ecart && <Row label="Écart">{fmtSigned(ctx.ecart)}</Row>}
                      {ctx.montantAjustement && (
                        <Row label="Montant ajustement">
                          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: isAdd ? '#0AD69F' : '#EF456E', textTransform: 'none' }}>
                            {isAdd ? '+' : '-'}{Number(ctx.montantAjustement).toLocaleString('fr-MA')} MAD
                          </Typography>
                        </Row>
                      )}
                      {ctx.note && <Row label="Note">{ctx.note}</Row>}
                    </Box>
                  );
                }

                return (
                  <Box>
                    {ctx.versementCode && <Row label="Versement">{ctx.versementCode}</Row>}
                    {ctx.versementAmount && <Row label="Montant versement">{fmtAmt(ctx.versementAmount)}</Row>}
                    {ctx.transactionsNet && <Row label="Transactions (net)">{fmtAmt(ctx.transactionsNet)}</Row>}
                    {ctx.ecartVT && <Row label="Écart versement/transactions">{fmtSigned(ctx.ecartVT)}</Row>}
                    {ctx.transactionsSales && <Row label="Ventes">{fmtAmt(ctx.transactionsSales)}</Row>}
                    {ctx.verificationsTotal && Number(ctx.verificationsTotal) > 0 && <Row label="Vérifications">{fmtAmt(ctx.verificationsTotal)}</Row>}
                    {ctx.ecartTV && <Row label="Écart transactions/vérification">{fmtSigned(ctx.ecartTV)}</Row>}
                    {ctx.montantAjustement && (
                      <Row label="Montant ajustement">
                        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: isAdd ? '#0AD69F' : '#EF456E', textTransform: 'none' }}>
                          {isAdd ? '+' : '-'}{Number(ctx.montantAjustement).toLocaleString('fr-MA')} MAD
                        </Typography>
                      </Row>
                    )}
                    {ctx.note && <Row label="Note">{ctx.note}</Row>}
                  </Box>
                );
              })()}

              {event.type === 'versement_context' && (() => {
                let ctx = {};
                try { ctx = JSON.parse(event.newValue || '{}'); } catch (e) {}
                const isAdd = ctx.direction === 'ADD';
                const fmtAmt = (v) => v ? Number(v).toLocaleString('fr-MA') + ' MAD' : '—';
                const fmtSigned = (v) => {
                  if (!v) return '—';
                  const n = Number(v);
                  const color = n >= 0 ? '#0AD69F' : '#EF456E';
                  const prefix = n >= 0 ? '+' : '';
                  return <Typography component="span" sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color, textTransform: 'none' }}>{prefix}{n.toLocaleString('fr-MA')} MAD</Typography>;
                };
                const Row = ({ label, children }) => (
                  <Box sx={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#445E76', textTransform: 'none' }}>{label} :</Typography>
                    {typeof children === 'string'
                      ? <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#000000', textTransform: 'none' }}>{children}</Typography>
                      : children}
                  </Box>
                );
                return (
                  <Box>
                    {ctx.versementCode && <Row label="Versement">{ctx.versementCode}</Row>}
                    {ctx.montantDeclare && <Row label="Montant déclaré">{fmtAmt(ctx.montantDeclare)}</Row>}
                    {ctx.montantConstate && <Row label="Montant constaté">{fmtAmt(ctx.montantConstate)}</Row>}
                    {ctx.ecart && <Row label="Écart">{fmtSigned(ctx.ecart)}</Row>}
                    {ctx.montantAjustement && (
                      <Row label="Montant ajustement">
                        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: isAdd ? '#0AD69F' : '#EF456E', textTransform: 'none' }}>
                          {isAdd ? '+' : '-'}{Number(ctx.montantAjustement).toLocaleString('fr-MA')} MAD
                        </Typography>
                      </Row>
                    )}
                    {ctx.note && <Row label="Note">{ctx.note}</Row>}
                  </Box>
                );
              })()}

              {event.type === 'versement_confirmed' && (() => {
                let ctx = {};
                try { ctx = JSON.parse(event.newValue || '{}'); } catch (e) {}
                const isAdd = ctx.direction === 'ADD';
                const fmtAmt = (v) => v ? Number(v).toLocaleString('fr-MA') + ' MAD' : '—';
                const fmtSigned = (v) => {
                  if (!v) return '—';
                  const n = Number(v);
                  const color = n >= 0 ? '#0AD69F' : '#EF456E';
                  const prefix = n >= 0 ? '+' : '';
                  return <Typography component="span" sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color, textTransform: 'none' }}>{prefix}{n.toLocaleString('fr-MA')} MAD</Typography>;
                };
                const Row = ({ label, children }) => (
                  <Box sx={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#445E76', textTransform: 'none' }}>{label} :</Typography>
                    {typeof children === 'string'
                      ? <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#000000', textTransform: 'none' }}>{children}</Typography>
                      : children}
                  </Box>
                );
                return (
                  <Box>
                    {ctx.montantDeclare && <Row label="Montant déclaré">{fmtAmt(ctx.montantDeclare)}</Row>}
                    {ctx.montantConstate && <Row label="Montant constaté">{fmtAmt(ctx.montantConstate)}</Row>}
                    {ctx.ecart && <Row label="Écart">{fmtSigned(ctx.ecart)}</Row>}
                    {ctx.montantAjustement && (
                      <Row label="Ajustement">
                        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: isAdd ? '#0AD69F' : '#EF456E', textTransform: 'none' }}>
                          {isAdd ? '+' : '-'}{Number(ctx.montantAjustement).toLocaleString('fr-MA')} MAD
                        </Typography>
                      </Row>
                    )}
                    {ctx.note && <Row label="Note">{ctx.note}</Row>}
                  </Box>
                );
              })()}

              {event.type === 'rapprochement_linked' && (
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#000000', textTransform: 'none' }}>
                  {transaction?.code === event.newValue
                    ? 'Versement rapproché'
                    : event.newValue ? `Inclus dans le rapprochement du versement ${event.newValue}` : 'Inclus dans un rapprochement'}
                </Typography>
              )}

              {/* Generic event display for other event types */}
              {event.type !== 'created' && event.type !== 'description_changed' && event.type !== 'verification_status_changed' && event.type !== 'refs_changed' && event.type !== 'rapprochement_context' && event.type !== 'versement_context' && event.type !== 'versement_confirmed' && event.type !== 'rapprochement_linked' && (
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#000000',
                    textTransform: 'none',
                    opacity: 1
                  }}
                >
                  {event.description || event.message || event.type}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </DetailsPanel>
  );
};

export default TransactionHistory;
